/** BindMeetingToMatterModal — 把一场会议关联到事项，并做可见范围二次确认。
 *
 * 关联事项在业务上是一次显式授权：会议由「仅本人可见」变为「事项成员可见」，
 * 该事项的全体在册成员都能查看这场会议的标题、时间、参会人、转录与摘要。
 * 因此弹窗分两步——先选事项，再展示「谁将因此看到这场会议」并确认。
 *
 * 由调用方在打开时挂载、关闭时卸载，因此每次打开都是全新的选择流程，
 * 不会残留上一次的事项与检索词。
 *
 * 三态：
 * - 选择事项：下拉候选（服务端关键词检索，弹窗打开后才拉取）
 * - 确认授权：展示所选事项的在册成员，等待用户确认
 * - 失败可重试：保存失败时保留在确认步骤，给出错误提示与重试入口
 */

import { useMemo, useState } from "react";
import { Alert, Button, Modal, Select, Typography } from "antd";
import { useDebouncedValue } from "../../hooks";
import {
  useMatterMembers,
  useMatters,
  useUpdateMeeting,
  useUsers,
} from "../../api/queries";

const { Paragraph, Text } = Typography;

/** 确认文案里最多列举的成员姓名个数，其余以「等 N 人」收口。 */
const MAX_NAMED_MEMBERS = 5;

interface BindMeetingToMatterModalProps {
  open: boolean;
  meetingId: string;
  /** 已关联的事项 id；有值时本次是「更换事项」。 */
  currentMatterId?: string | null;
  onClose: () => void;
  onBound?: (matterId: string) => void;
}

export default function BindMeetingToMatterModal({
  open,
  meetingId,
  currentMatterId,
  onClose,
  onBound,
}: BindMeetingToMatterModalProps) {
  const [step, setStep] = useState<"select" | "confirm">("select");
  const [matterId, setMatterId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [errorText, setErrorText] = useState<string | null>(null);

  const updateMut = useUpdateMeeting();

  const debouncedSearch = useDebouncedValue(search.trim());
  const mattersQuery = useMatters(
    {
      page: 1,
      pageSize: 100,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    },
    { enabled: open },
  );
  const mattersData = mattersQuery.data;
  const matterOptions = useMemo(
    () =>
      (mattersData?.data ?? [])
        .filter((m) => m.id !== currentMatterId)
        .map((m) => ({ label: m.name, value: m.id })),
    [mattersData, currentMatterId],
  );
  const mattersTruncated =
    (mattersData?.meta?.total ?? 0) > (mattersData?.data?.length ?? 0);
  const selectedMatterName =
    (mattersData?.data ?? []).find((m) => m.id === matterId)?.name ?? "该事项";

  // 成员名单只在进入确认步骤后才需要，用于把「谁会看到」讲具体。
  const membersQuery = useMatterMembers(
    step === "confirm" && matterId ? matterId : "",
  );
  const { data: usersData } = useUsers({ pageSize: 100 });
  const userNameMap = useMemo(
    () => new Map((usersData?.data ?? []).map((u) => [u.id, u.name])),
    [usersData],
  );
  const activeMembers = useMemo(
    () => (membersQuery.data ?? []).filter((m) => !m.leftAt),
    [membersQuery.data],
  );
  const memberNames = useMemo(
    () =>
      activeMembers
        .map((m) => userNameMap.get(m.userId))
        .filter((n): n is string => !!n),
    [activeMembers, userNameMap],
  );

  const handleSubmit = async () => {
    if (!matterId) return;
    setErrorText(null);
    try {
      await updateMut.mutateAsync({ id: meetingId, data: { matterId } });
      onBound?.(matterId);
      onClose();
    } catch (err) {
      setErrorText(
        err instanceof Error && err.message
          ? err.message
          : "保存失败，请稍后重试",
      );
    }
  };

  const footer =
    step === "select"
      ? [
          <Button key="cancel" onClick={onClose}>
            取消
          </Button>,
          <Button
            key="next"
            type="primary"
            disabled={!matterId}
            onClick={() => setStep("confirm")}
          >
            下一步
          </Button>,
        ]
      : [
          <Button key="back" onClick={() => setStep("select")} disabled={updateMut.isPending}>
            返回修改
          </Button>,
          <Button
            key="ok"
            type="primary"
            loading={updateMut.isPending}
            onClick={handleSubmit}
          >
            {errorText ? "重试" : "确认关联"}
          </Button>,
        ];

  return (
    <Modal
      title={step === "select" ? "关联到事项" : "确认关联事项"}
      open={open}
      onCancel={onClose}
      footer={footer}
      destroyOnHidden
      width={520}
    >
      {step === "select" ? (
        <>
          <Paragraph type="secondary">
            关联事项后，这场会议将对该事项的全体在册成员可见。下一步会列出具体成员再确认。
          </Paragraph>
          {mattersQuery.isError ? (
            <div role="alert">
              <Text type="danger">事项列表加载失败</Text>
              <Button
                type="link"
                size="small"
                onClick={() => void mattersQuery.refetch()}
              >
                重试
              </Button>
            </div>
          ) : (
            <>
              <Select
                style={{ width: "100%" }}
                placeholder="选择事项（须为该事项服务团队成员）"
                aria-label="选择事项"
                data-testid="bind-meeting-matter-select"
                // 候选上限 100 条，关闭虚拟滚动让候选项完整落在 DOM 里。
                virtual={false}
                options={matterOptions}
                value={matterId}
                onChange={setMatterId}
                loading={mattersQuery.isFetching}
                showSearch
                // 服务端检索：关闭客户端过滤，服务端还会匹配事项编号。
                filterOption={false}
                onSearch={setSearch}
                notFoundContent={
                  debouncedSearch && !mattersQuery.isFetching
                    ? "未找到匹配的事项，可更换关键词"
                    : undefined
                }
              />
              {mattersTruncated && (
                <Paragraph
                  type="secondary"
                  style={{ marginTop: "var(--jerry-sp-2)", fontSize: "var(--jerry-font-size-base)" }}
                >
                  {debouncedSearch
                    ? "匹配事项较多，仅显示前 100 条，可换更精确的关键词"
                    : "事项较多，仅显示最近更新的前 100 条，可输入关键词检索"}
                </Paragraph>
              )}
            </>
          )}
        </>
      ) : (
        <>
          <Paragraph>
            关联到「{selectedMatterName}」后，
            {membersQuery.isLoading || memberNames.length === 0
              ? "该事项的全体在册成员"
              : `该事项的 ${activeMembers.length} 位在册成员`}
            都能查看这场会议的标题、时间、参会人、转录与摘要。
          </Paragraph>
          {memberNames.length > 0 && (
            <Paragraph type="secondary" data-testid="bind-meeting-member-names">
              将看到这场会议的成员：
              {memberNames.slice(0, MAX_NAMED_MEMBERS).join("、")}
              {memberNames.length > MAX_NAMED_MEMBERS
                ? ` 等 ${memberNames.length} 人`
                : ""}
            </Paragraph>
          )}
          <Paragraph type="secondary">
            这是一次显式授权，不是简单归类：会议此前仅您本人可见，关联后不再限于本人。
            若这场会议涉及不便向事项团队公开的内容，请先确认再关联。
          </Paragraph>
          {errorText && (
            <Alert
              type="error"
              showIcon
              message="关联未生效"
              description={errorText}
              style={{ marginTop: "var(--jerry-sp-3)" }}
            />
          )}
        </>
      )}
    </Modal>
  );
}
